import type {
  CreateMcpCredentialMutationVariables,
  CreateUserMcpCredentialMutationVariables,
  UpdateMcpCredentialWorkspaceGrantsForUserMutationVariables,
} from '@affine/graphql';
import {
  createMcpCredentialMutation,
  createUserMcpCredentialMutation,
  deleteMcpCredentialForUserMutation,
  mcpCredentialsForUserQuery,
  mcpCredentialsQuery,
  revokeMcpCredentialForUserMutation,
  revokeMcpCredentialMutation,
  revealMcpCredentialForUserMutation,
  rotateMcpCredentialForUserMutation,
  rotateMcpCredentialMutation,
  updateMcpCredentialWorkspaceGrantsForUserMutation,
  updateMcpCredentialWorkspaceGrantsMutation,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { GraphQLService } from '../services/graphql';

export class McpCredentialStore extends Store {
  constructor(private readonly gqlService: GraphQLService) {
    super();
  }

  async list(workspaceId: string, signal?: AbortSignal) {
    const data = await this.gqlService.gql({
      query: mcpCredentialsQuery,
      variables: { workspaceId },
      context: { signal },
    });
    return data;
  }

  async listForUser(signal?: AbortSignal) {
    return await this.gqlService.gql({
      query: mcpCredentialsForUserQuery,
      context: { signal },
    });
  }

  async create(input: CreateMcpCredentialMutationVariables['input']) {
    const data = await this.gqlService.gql({
      query: createMcpCredentialMutation,
      variables: {
        input: {
          workspaceId: input.workspaceId,
          name: input.name,
          accessMode: input.accessMode,
          scopes: input.scopes,
          workspaceGrants: input.workspaceGrants,
          expirationDays: input.expirationDays,
        },
      },
    });
    return data.createMcpCredential;
  }

  async createForUser(
    input: CreateUserMcpCredentialMutationVariables['input']
  ) {
    const data = await this.gqlService.gql({
      query: createUserMcpCredentialMutation,
      variables: {
        input: {
          workspaceId: input.workspaceId,
          name: input.name,
          accessMode: input.accessMode,
          scopes: input.scopes,
          workspaceGrants: input.workspaceGrants,
          expirationDays: input.expirationDays ?? 90,
        },
      },
    });
    return data.createUserMcpCredential;
  }

  async rotate(id: string, workspaceId: string, expirationDays: number) {
    const data = await this.gqlService.gql({
      query: rotateMcpCredentialMutation,
      variables: { id, workspaceId, expirationDays },
    });
    return data.rotateMcpCredential;
  }

  async rotateForUser(id: string, expirationDays: number) {
    const data = await this.gqlService.gql({
      query: rotateMcpCredentialForUserMutation,
      variables: { id, expirationDays },
    });
    return data.rotateMcpCredentialForUser;
  }

  async revealForUser(id: string) {
    const data = await this.gqlService.gql({
      query: revealMcpCredentialForUserMutation,
      variables: { id },
    });
    return data.revealMcpCredentialForUser;
  }

  async revoke(id: string, workspaceId: string) {
    const data = await this.gqlService.gql({
      query: revokeMcpCredentialMutation,
      variables: { id, workspaceId },
    });
    return data.revokeMcpCredential;
  }

  async revokeForUser(id: string) {
    const data = await this.gqlService.gql({
      query: revokeMcpCredentialForUserMutation,
      variables: { id },
    });
    return data.revokeMcpCredentialForUser;
  }

  async deleteForUser(id: string) {
    const data = await this.gqlService.gql({
      query: deleteMcpCredentialForUserMutation,
      variables: { id },
    });
    return data.deleteMcpCredentialForUser;
  }

  async updateWorkspaceGrants(
    id: string,
    workspaceId: string,
    workspaceGrants: CreateMcpCredentialMutationVariables['input']['workspaceGrants']
  ) {
    const data = await this.gqlService.gql({
      query: updateMcpCredentialWorkspaceGrantsMutation,
      variables: {
        id,
        workspaceId,
        workspaceGrants: workspaceGrants ?? [],
      },
    });
    return data.updateMcpCredentialWorkspaceGrants;
  }

  async updateWorkspaceGrantsForUser(
    id: string,
    workspaceGrants: UpdateMcpCredentialWorkspaceGrantsForUserMutationVariables['workspaceGrants']
  ) {
    const data = await this.gqlService.gql({
      query: updateMcpCredentialWorkspaceGrantsForUserMutation,
      variables: { id, workspaceGrants },
    });
    return data.updateMcpCredentialWorkspaceGrantsForUser;
  }
}
